import { collection, doc, getDoc, getDocs, addDoc, updateDoc, deleteDoc, onSnapshot, QuerySnapshot, DocumentData, serverTimestamp } from 'firebase/firestore';
import { db } from '../config';
import { Role, PermissionId, PermissionCheckResult } from '../../types/rbac';
import { isValidPermissionId } from './permissionService';

const rolesRef = collection(db, 'roles');

/**
 * Convert Firestore data to Role type
 */
function convertToRole(id: string, data: DocumentData): Role {
  return {
    id,
    name: data.name,
    description: data.description,
    grantedPermissions: data.grantedPermissions || [],
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date()
  };
}

/**
 * Get all roles from Firestore
 */
export async function getRoles(): Promise<Role[]> {
  const snapshot = await getDocs(rolesRef);
  return snapshot.docs.map(doc => convertToRole(doc.id, doc.data()));
}

/**
 * Get a role by its ID
 */
export async function getRoleById(id: string): Promise<Role | null> {
  const docRef = doc(rolesRef, id);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? convertToRole(docSnap.id, docSnap.data()) : null;
}

/**
 * Create a new role
 */
export async function createRole(role: Omit<Role, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  // Validate permissions
  const invalidPermissions = role.grantedPermissions.filter(p => !isValidPermissionId(p));
  if (invalidPermissions.length > 0) {
    throw new Error(`Invalid permissions: ${invalidPermissions.join(', ')}`);
  }

  const docRef = await addDoc(rolesRef, {
    ...role,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Update an existing role
 */
export async function updateRole(id: string, updates: Partial<Omit<Role, 'id' | 'createdAt'>>): Promise<void> {
  // Validate permissions if they're being updated
  if (updates.grantedPermissions) {
    const invalidPermissions = updates.grantedPermissions.filter(p => !isValidPermissionId(p));
    if (invalidPermissions.length > 0) {
      throw new Error(`Invalid permissions: ${invalidPermissions.join(', ')}`);
    }
  }

  const docRef = doc(rolesRef, id);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });
}

/**
 * Delete a role
 */
export async function deleteRole(id: string): Promise<void> {
  const docRef = doc(rolesRef, id);
  await deleteDoc(docRef);
}

/**
 * Subscribe to role changes
 */
export function subscribeToRoles(callback: (roles: Role[]) => void): () => void {
  return onSnapshot(rolesRef, (snapshot: QuerySnapshot<DocumentData>) => {
    const roles = snapshot.docs.map(doc => convertToRole(doc.id, doc.data()));
    callback(roles);
  });
}

/**
 * Check if a role has a specific permission
 */
export function checkPermission(role: Role, permissionId: PermissionId): PermissionCheckResult {
  if (!isValidPermissionId(permissionId)) {
    return {
      granted: false,
      reason: 'Invalid permission ID'
    };
  }

  return {
    granted: role.grantedPermissions.includes(permissionId)
  };
}

/**
 * Check if a role has all specified permissions
 */
export function checkPermissions(role: Role, permissionIds: PermissionId[]): PermissionCheckResult {
  const invalidPermissions = permissionIds.filter(p => !isValidPermissionId(p));
  if (invalidPermissions.length > 0) {
    return {
      granted: false,
      reason: `Invalid permission IDs: ${invalidPermissions.join(', ')}`
    };
  }

  const missingPermissions = permissionIds.filter(p => !role.grantedPermissions.includes(p));
  return {
    granted: missingPermissions.length === 0,
    reason: missingPermissions.length > 0 ? `Missing permissions: ${missingPermissions.join(', ')}` : undefined
  };
} 